// =============================================
// useCloudflareAccounts — CRUD kont Cloudflare (token API per konto)
//
// Tabela cloudflare_accounts, RLS is_allowed_user(). Token to sekret — trzymany
// w DB, dosylany do CF przez proxy /api/cloudflare/ (patrz cloudflareService).
// =============================================
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export interface CloudflareAccount {
  id: string;
  user_id: string | null;
  label: string;
  api_token: string;
  account_id?: string | null;
  email?: string | null;
  created_at: string;
}

export interface NewCloudflareAccount {
  label: string;
  api_token: string;
  account_id?: string;
  email?: string;
}

export const useCloudflareAccounts = () => {
  const [accounts, setAccounts] = useState<CloudflareAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from("cloudflare_accounts")
        .select("*")
        .order("label", { ascending: true });
      if (error) throw error;
      setAccounts((data || []) as CloudflareAccount[]);
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : "Nie udało się pobrać kont Cloudflare",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (user?.id) fetchAccounts();
  }, [user?.id, fetchAccounts]);

  const addAccount = useCallback(
    async (data: NewCloudflareAccount): Promise<boolean> => {
      try {
        const { error } = await (supabase as any).from("cloudflare_accounts").insert({
          user_id: user?.id ?? null,
          label: data.label.trim(),
          api_token: data.api_token.trim(),
          account_id: data.account_id?.trim() || null,
          email: data.email?.trim() || null,
        });
        if (error) throw error;
        await fetchAccounts();
        toast({ title: "Dodano konto Cloudflare", description: data.label });
        return true;
      } catch (err) {
        toast({
          title: "Błąd",
          description: err instanceof Error ? err.message : "Nie udało się dodać konta",
          variant: "destructive",
        });
        return false;
      }
    },
    [user?.id, fetchAccounts, toast],
  );

  const updateAccount = useCallback(
    async (id: string, patch: Partial<NewCloudflareAccount>): Promise<boolean> => {
      try {
        const { error } = await (supabase as any)
          .from("cloudflare_accounts")
          .update({ ...patch, updated_at: new Date().toISOString() })
          .eq("id", id);
        if (error) throw error;
        await fetchAccounts();
        return true;
      } catch (err) {
        toast({
          title: "Błąd",
          description: err instanceof Error ? err.message : "Nie udało się zapisać konta",
          variant: "destructive",
        });
        return false;
      }
    },
    [fetchAccounts, toast],
  );

  const deleteAccount = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error } = await (supabase as any).from("cloudflare_accounts").delete().eq("id", id);
        if (error) throw error;
        setAccounts((prev) => prev.filter((a) => a.id !== id));
        toast({ title: "Usunięto konto Cloudflare" });
        return true;
      } catch (err) {
        toast({
          title: "Błąd",
          description: err instanceof Error ? err.message : "Nie udało się usunąć konta",
          variant: "destructive",
        });
        return false;
      }
    },
    [toast],
  );

  return { accounts, loading, addAccount, updateAccount, deleteAccount, refreshAccounts: fetchAccounts };
};
